import React, { useState, useEffect } from 'react'
import { Card, CardBody, Row, Col, FormGroup, Label, Input, Button, Alert } from 'reactstrap'
import { useNavigate, useParams } from 'react-router-dom'
import { getDataWithTypeForSelect } from '../../lib/parameters'
import { addData, updateData, getIdData } from '../../lib/resourcecenter'
import { useAuth } from '../../_helper/Auth/AuthProvider'
import { useLayout } from '../../Hooks/useLayout'
import { getCurrentDateInput } from '../../utils/dateTimeUtils'

const InstructorResourceEdit = () => {
	const [rdate, setRdate] = useState('')
	const [topic, setTopic] = useState('')
	const [parameters, setParameters] = useState([])
	const [grade, setGrade] = useState('')
	const [howuse, setHowuse] = useState('')
	const [link1, setLink1] = useState('')
	const [shareWithStudents, setShareWithStudents] = useState(false)
	const [recId, setRecId] = useState(0)
	const [error, setError] = useState('')
	const [loading, setLoading] = useState(false)
	const [initialLoading, setInitialLoading] = useState(true)
	const [isDarkMode, setIsDarkMode] = useState(false)

	const { user } = useAuth()
	const navigate = useNavigate()
	const layout = useLayout()
	const { id } = useParams()

	const instructorId = user?.id

	const getParameterData = async () => {
		try {
			const info = await getDataWithTypeForSelect('RESOURCE')
			setParameters(info)
		} catch (error) {
			console.error('Error fetching parameters:', error)
			setError('Failed to load parameters')
		}
	}

	const getResourceData = async (resourceId) => {
		if (!resourceId) {
			setRdate(getCurrentDateInput())
			setInitialLoading(false)
			return
		}

		try {
			setInitialLoading(true)
			const data = await getIdData(resourceId)
			if (data && data.length > 0) {
				setRecId(data[0].id)
				setRdate(data[0].rdate)
				setTopic(data[0].topic)
				setGrade(data[0].grade)
				setHowuse(data[0].howuse)
				setLink1(data[0].link1)
			} else {
				setRdate(getCurrentDateInput())
			}
		} catch (error) {
			console.error('Error fetching resource data:', error)
			setError('Failed to load resource data')
		} finally {
			setInitialLoading(false)
		}
	}

	const handleSubmit = async (e) => {
		e.preventDefault()
		setLoading(true)
		setError('')

		try {
			if (recId > 0) {
				await updateData({
					id: recId,
					instructorId: instructorId,
					rdate: new Date(rdate),
					topic: topic,
					grade: grade,
					howuse: howuse,
					link1: link1
				})
			} else {
				await addData({
					instructorId: instructorId,
					rdate: new Date(rdate),
					topic: topic,
					grade: grade,
					howuse: howuse,
					link1: link1
				})
			}
			navigate(`${process.env.PUBLIC_URL}/instructor/resources/${layout}`)
		} catch (error) {
			console.error('Error submitting form:', error)
			setError('Failed to save resource')
		} finally {
			setLoading(false)
		}
	}

	const handleCancel = () => {
		navigate(`${process.env.PUBLIC_URL}/instructor/resources/${layout}`)
	}

	useEffect(() => {
		getParameterData()
		if (id) {
			getResourceData(id)
		} else {
			setRdate(getCurrentDateInput())
			setInitialLoading(false)
		}
	}, [id])

	useEffect(() => {
		const checkDarkMode = () => {
			setIsDarkMode(document.body.classList.contains('dark-only'))
		}
		checkDarkMode()
		const observer = new MutationObserver(checkDarkMode)
		observer.observe(document.body, { attributes: true, attributeFilter: ['class'] })
		return () => observer.disconnect()
	}, [])

	if (initialLoading) {
		return (
			<Card>
				<CardBody>
					<div className="text-center">
						<div className="spinner-border" role="status">
							<span className="visually-hidden">Loading...</span>
						</div>
						<p className="mt-2">Loading resource data...</p>
					</div>
				</CardBody>
			</Card>
		)
	}

	const isAddMode = !id || id === '0'

	return (
		<Card>
			<CardBody>
				<div className="d-flex justify-content-between align-items-center mb-4">
					<h3 style={{ color: isDarkMode ? '#fff' : 'inherit' }}>{isAddMode ? 'Add Resource' : 'Update Resource'}</h3>
				</div>

				{error && (
					<Alert color="danger" className="alert-dismissible fade show" role="alert">
						<strong>Error:</strong> {error}
						<Button type="button" className="btn-close" onClick={() => setError('')} aria-label="Close"></Button>
					</Alert>
				)}

				<form onSubmit={handleSubmit}>
					<Row>
						<Col md="6">
							<FormGroup>
								<Label className="form-control-label" style={{ color: isDarkMode ? '#fff' : 'inherit' }}>Date</Label>
								<Input type="date" value={rdate} onChange={(e) => setRdate(e.target.value)} required disabled={loading} />
							</FormGroup>
						</Col>
						<Col md="6">
							<FormGroup>
								<Label className="form-control-label" style={{ color: isDarkMode ? '#fff' : 'inherit' }}>Topic</Label>
								<Input type="select" value={topic} onChange={(e) => setTopic(e.target.value)} required disabled={loading}>
									<option value="">Select Topic</option>
									{parameters.map((param) => (
										<option key={param.value} value={param.value}>
											{param.label}
										</option>
									))}
								</Input>
							</FormGroup>
						</Col>
					</Row>

					<Row>
						<Col md="6">
							<FormGroup>
								<Label className="form-control-label" style={{ color: isDarkMode ? '#fff' : 'inherit' }}>Grade</Label>
								<Input type="select" value={grade} onChange={(e) => setGrade(e.target.value)} required disabled={loading}>
									<option value="">Select Grade</option>
									<option value="12">12</option>
									<option value="9-11">9-11</option>
									<option value="All">All</option>
								</Input>
							</FormGroup>
						</Col>
						<Col md="6">
							<FormGroup>
								<Label className="form-control-label" style={{ color: isDarkMode ? '#fff' : 'inherit' }}>Link</Label>
								<Input type="url" value={link1} onChange={(e) => setLink1(e.target.value)} placeholder="Enter resource link" disabled={loading} />
							</FormGroup>
						</Col>
					</Row>

					<Row>
						<Col md="12">
							<FormGroup>
								<Label className="form-control-label" style={{ color: isDarkMode ? '#fff' : 'inherit' }}>How to use it</Label>
								<Input
									type="textarea"
									value={howuse}
									onChange={(e) => setHowuse(e.target.value)}
									placeholder="Describe how to use this resource"
									rows="4"
									required
									disabled={loading}
								/>
							</FormGroup>
						</Col>
					</Row>

					<Row>
						<Col md="12">
							<FormGroup>
								<div className="form-check form-switch">
									<Input
										type="checkbox"
										name="shareWithStudents"
										checked={shareWithStudents}
										onChange={(e) => setShareWithStudents(e.target.checked)}
										disabled={loading}
										style={{
											border: !shareWithStudents ? '2px solid #6c757d' : 'none',
											opacity: loading ? 0.6 : 1
										}}
									/>
									<Label className="form-check-label" style={{ color: isDarkMode ? '#fff' : 'inherit' }}>Share with my students</Label>
								</div>
							</FormGroup>
						</Col>
					</Row>

					<div className="d-flex justify-content-between mt-4">
						<Button type="submit" color="primary" disabled={loading}>
							{loading ? 'Saving...' : isAddMode ? 'Add Resource' : 'Update Resource'}
						</Button>
						<Button type="button" color="secondary" onClick={handleCancel} disabled={loading}>
							Cancel
						</Button>
					</div>
				</form>
			</CardBody>
		</Card>
	)
}

export { InstructorResourceEdit }
